import { ExternalLink } from 'lucide-react';

interface OfficialSourceLink {
  label: string;
  href: string;
  description: string;
}

interface OfficialSourcesNoticeProps {
  title?: string;
  description?: string;
  compact?: boolean;
  className?: string;
  extraLinks?: OfficialSourceLink[];
}

const baseLinks: OfficialSourceLink[] = [
  {
    label: 'Krisinformation.se',
    href: 'https://www.krisinformation.se',
    description: 'Officiell krisinformation och VMA från ansvariga myndigheter.',
  },
  {
    label: 'Myndigheten för civilt försvar',
    href: 'https://www.mcf.se',
    description: 'Officiella råd om hemberedskap, civilt försvar och skydd.',
  },
  {
    label: 'DinSäkerhet.se',
    href: 'https://www.dinsakerhet.se',
    description: 'Praktiska råd om säkerhet och beredskap för privatpersoner.',
  },
];

function dedupeLinks(links: OfficialSourceLink[]): OfficialSourceLink[] {
  const seen = new Set<string>();

  return links.filter((link) => {
    if (seen.has(link.href)) {
      return false;
    }

    seen.add(link.href);
    return true;
  });
}

export default function OfficialSourcesNotice({
  title = 'Officiella källor och ansvarsfriskrivning',
  description = 'Beredskapshubben samlar vägledande information om hemberedskap. Vid kris eller nödläge ska du alltid följa aktuella instruktioner från officiella källor.',
  compact = false,
  className = '',
  extraLinks = [],
}: OfficialSourcesNoticeProps) {
  const links = dedupeLinks([...baseLinks, ...extraLinks]);

  return (
    <section className={`rounded-xl border border-amber-500/30 bg-amber-500/10 ${compact ? 'p-4' : 'p-6'} ${className}`.trim()}>
      <div className="space-y-3">
        <div>
          <h3 className={`${compact ? 'text-base' : 'text-lg'} font-semibold text-foreground`}>{title}</h3>
          <p className={`${compact ? 'mt-1 text-sm' : 'mt-2 text-sm'} text-muted-foreground`}>
            {description}
          </p>
        </div>

        <div className="rounded-lg border border-amber-500/20 bg-background/70 p-3 text-sm text-foreground">
          Beredskapshubben är en privat tjänst från Beredskapshubben Norden AB. Appen är inte en officiell myndighetsapp och företräder inte Myndigheten för civilt försvar, Krisinformation.se eller någon annan svensk myndighet.
        </div>

        <div className="space-y-3">
          {links.map((link) => (
            <a
              key={link.href}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className="block rounded-lg border border-border bg-card/80 p-3 transition-colors hover:border-primary/40 hover:bg-card"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium text-foreground">{link.label}</div>
                  <div className="mt-1 text-sm text-muted-foreground">{link.description}</div>
                  <div className="mt-2 soft-wrap text-xs text-primary">{link.href}</div>
                </div>
                <ExternalLink className="h-4 w-4 shrink-0 text-primary" />
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}